/**
 * /settings/offline — Offline Maps manager.
 *
 * Powered by the same IndexedDB store (`offlineStore.js`) that the map uses
 * to save route packs. This page adds:
 *   • Full list of downloaded route packs with size + age
 *   • Refresh (re-fetches the corridor while online), delete, or open on map
 *   • Storage overview + Wi-Fi-only auto-refresh toggle (preference is stored
 *     locally — no server round-trip needed)
 *   • Max cache size setting (informational for now; enforced in the store
 *     via LRU trim when new packs are added)
 *   • Pending Sync queue viewer (Slice 3 handoff)
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ChevronLeft, Trash2, RefreshCw, Map as MapIcon, HardDrive, Wifi, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  listRoutePacks, deleteRoutePack, saveRoutePack, estimateOfflineStorage,
} from "@/lib/offlineStore";
import { usePendingSync } from "@/lib/syncManager";
import { deleteQueuedAction } from "@/lib/offlineStore";
import { useOnline, relativeTimeFrom } from "@/lib/useOnline";
import { api } from "@/lib/api";

const LS_PREFS = "fn_offline_prefs";

function loadPrefs() {
  try { return JSON.parse(localStorage.getItem(LS_PREFS)) || {}; } catch { return {}; }
}
function savePrefs(p) { localStorage.setItem(LS_PREFS, JSON.stringify(p)); }

export default function OfflineMaps() {
  const [packs,    setPacks]    = useState([]);
  const [storage,  setStorage]  = useState({ count: 0, bytes: 0, cap_mb: 200 });
  const [prefs,    setPrefs]    = useState(loadPrefs);
  const [refreshing, setRefreshing] = useState(null);
  const { online } = useOnline();
  const { rows: queued, refresh: refreshQueue } = usePendingSync();

  const reload = async () => {
    setPacks(await listRoutePacks());
    setStorage(await estimateOfflineStorage());
  };
  useEffect(() => { reload(); }, []);

  const changePref = (key, val) => {
    const next = { ...prefs, [key]: val };
    setPrefs(next); savePrefs(next);
    toast.success("Preference saved.");
  };

  const remove = async (pack) => {
    if (!window.confirm(`Delete "${pack.name}"? This can't be undone.`)) return;
    await deleteRoutePack(pack.pack_id);
    reload();
    toast.success("Route pack deleted.");
  };

  const refreshPack = async (pack) => {
    if (!online) { toast.error("You need to be online to refresh a pack."); return; }
    if (!pack.origin || !pack.destination) { toast.error("Missing origin/destination."); return; }
    setRefreshing(pack.pack_id);
    try {
      const r = await api.post("/discover/along-route", {
        origin:      pack.origin,
        destination: pack.destination,
        waypoints:   pack.waypoints || [],
        categories:  pack.categories || ["washroom", "cafe"],
        corridor_km: pack.corridor_km || 1.0,
        total_limit: 40,
      });
      await saveRoutePack({
        ...pack,
        route:         r.data.route,
        pois:          r.data.pois || [],
        size_estimate: JSON.stringify(r.data).length,
      });
      await reload();
      toast.success(`"${pack.name}" refreshed with latest data.`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Refresh failed.");
    } finally { setRefreshing(null); }
  };

  const bytesLabel = (b) => b < 1024 ? `${b} B` : b < 1024 * 1024 ? `${(b / 1024).toFixed(1)} KB` : `${(b / 1024 / 1024).toFixed(2)} MB`;
  const capPct = Math.min(100, (storage.bytes / (storage.cap_mb * 1024 * 1024)) * 100);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 to-slate-900 text-white pb-24" data-testid="offline-maps-page">
      <div className="max-w-2xl mx-auto p-4 space-y-4">
        <div className="flex items-center gap-2">
          <Link to="/settings" className="rounded-full bg-white/10 hover:bg-white/20 p-1.5" data-testid="offline-back">
            <ChevronLeft size={14}/>
          </Link>
          <div>
            <h1 className="text-lg font-black">Offline Maps</h1>
            <div className="text-[11px] text-white/60">
              Pre-download A → B corridors so the app works when you lose signal.
            </div>
          </div>
        </div>

        {/* Storage overview */}
        <div className="rounded-3xl bg-gradient-to-br from-sky-500/20 to-slate-800 border border-sky-400/20 p-4" data-testid="offline-storage-summary">
          <div className="flex items-center gap-2 mb-1">
            <HardDrive size={14} className="text-sky-300"/>
            <div className="text-[11px] uppercase font-black text-sky-200">Storage</div>
          </div>
          <div className="flex items-baseline gap-2">
            <div className="text-2xl font-black tabular-nums">{bytesLabel(storage.bytes)}</div>
            <div className="text-[11px] text-white/50">of {storage.cap_mb} MB cap · {storage.count} pack{storage.count === 1 ? "" : "s"}</div>
          </div>
          <div className="mt-2 h-2 rounded-full bg-white/10 overflow-hidden">
            <div className="h-full bg-sky-400" style={{ width: `${capPct}%` }}/>
          </div>
        </div>

        {/* Prefs */}
        <div className="rounded-3xl bg-white/5 border border-white/10 p-4 space-y-3" data-testid="offline-prefs">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Wifi size={14} className="text-emerald-300"/>
              <div>
                <div className="text-[12px] font-black">Auto-refresh on Wi-Fi only</div>
                <div className="text-[10px] text-white/50">Skip refreshes when you're on cellular.</div>
              </div>
            </div>
            <Switch checked={!!prefs.wifi_only} onCheckedChange={v => changePref("wifi_only", v)}
                    data-testid="offline-pref-wifi-only"/>
          </div>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Clock size={14} className="text-amber-300"/>
              <div>
                <div className="text-[12px] font-black">Auto-refresh saved packs</div>
                <div className="text-[10px] text-white/50">Every 3 days when the app is opened online.</div>
              </div>
            </div>
            <Switch checked={!!prefs.auto_refresh} onCheckedChange={v => changePref("auto_refresh", v)}
                    data-testid="offline-pref-auto-refresh"/>
          </div>
        </div>

        {/* Saved packs */}
        <div className="rounded-3xl bg-white/5 border border-white/10 p-4" data-testid="offline-packs-list">
          <div className="flex items-center justify-between mb-2">
            <div className="text-[11px] uppercase font-black text-white/70">Downloaded routes</div>
            <div className="text-[10px] text-white/40">{packs.length} saved</div>
          </div>
          {packs.length === 0 ? (
            <div className="text-center py-6" data-testid="offline-packs-empty">
              <div className="text-3xl" aria-hidden>🗺️</div>
              <div className="mt-1 text-[12px] font-black">No offline routes yet.</div>
              <div className="text-[11px] text-white/50 mt-1">
                Open the Personal Map, plan an A → B route with corridor search, then tap <b>Save for offline use</b>.
              </div>
              <Link to="/nearby"
                    className="mt-3 inline-block rounded-full bg-emerald-500 hover:bg-emerald-600 text-slate-900 font-black text-[11px] px-4 py-2"
                    data-testid="offline-packs-empty-cta">
                Go to Personal Map
              </Link>
            </div>
          ) : (
            <div className="space-y-2">
              {packs.map(p => (
                <div key={p.pack_id} className="rounded-2xl bg-slate-800/60 p-3 border border-white/10"
                     data-testid={`offline-pack-${p.pack_id}`}>
                  <div className="flex items-start gap-2">
                    <MapIcon size={14} className="text-emerald-300 mt-0.5 shrink-0"/>
                    <div className="flex-1 min-w-0">
                      <div className="text-[12px] font-black text-white truncate">{p.name}</div>
                      <div className="text-[10px] text-white/50 mt-0.5 flex flex-wrap gap-x-2 gap-y-0.5">
                        <span>{(p.pois?.length || 0)} POIs</span>
                        <span>· {(p.route?.distance_km || 0).toFixed(1)} km</span>
                        <span>· {bytesLabel(p.size_estimate || 0)}</span>
                        <span>· saved {relativeTimeFrom(new Date(p.saved_at).toISOString())}</span>
                      </div>
                      <div className="text-[10px] text-white/40 mt-0.5">
                        {(p.categories || []).join(" · ") || "no categories"}
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-1 mt-2">
                    <Button onClick={() => refreshPack(p)} disabled={refreshing === p.pack_id || !online}
                            data-testid={`offline-pack-refresh-${p.pack_id}`}
                            className="rounded-full bg-white/10 hover:bg-white/20 text-white font-black text-[10px] h-7 flex-1 disabled:opacity-40">
                      <RefreshCw size={10} className="mr-1"/>{refreshing === p.pack_id ? "Refreshing…" : "Refresh"}
                    </Button>
                    <Link to="/nearby"
                          state={{ loadPack: p.pack_id }}
                          className="rounded-full bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-200 font-black text-[10px] h-7 flex-1 grid place-items-center"
                          data-testid={`offline-pack-open-${p.pack_id}`}>
                      Open on map
                    </Link>
                    <Button onClick={() => remove(p)}
                            data-testid={`offline-pack-delete-${p.pack_id}`}
                            className="rounded-full bg-rose-500/20 hover:bg-rose-500/30 text-rose-200 font-black text-[10px] h-7 px-3">
                      <Trash2 size={10}/>
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Pending sync queue (Slice 3 handoff) */}
        <div className="rounded-3xl bg-white/5 border border-white/10 p-4" data-testid="offline-sync-queue">
          <div className="flex items-center justify-between mb-2">
            <div className="text-[11px] uppercase font-black text-white/70">Pending sync</div>
            <div className="text-[10px] text-white/40">{queued.length} queued</div>
          </div>
          {queued.length === 0 ? (
            <div className="text-[11px] text-white/50 text-center py-3" data-testid="offline-sync-queue-empty">
              Nothing waiting to sync — everything is up to date.
            </div>
          ) : (
            <div className="space-y-1">
              {queued.slice(0, 20).map(q => (
                <div key={q.id} className="flex items-center gap-2 rounded-xl bg-slate-900/60 p-2 text-[10.5px]"
                     data-testid={`offline-queue-item-${q.id}`}>
                  <div className="rounded-full bg-amber-500/20 text-amber-300 text-[8.5px] font-black uppercase px-1.5 py-0.5">Pending</div>
                  <div className="flex-1 min-w-0">
                    <div className="text-white font-black truncate">{q.label}</div>
                    <div className="text-white/40 text-[9px]">
                      queued {relativeTimeFrom(new Date(q.created_at).toISOString())}
                      {q.retries > 0 && ` · retries ${q.retries}`}
                      {q.last_error && ` · ${q.last_error}`}
                    </div>
                  </div>
                  <button onClick={async () => { await deleteQueuedAction(q.id); await refreshQueue(); toast.success("Removed."); }}
                          className="text-white/40 hover:text-rose-300"
                          data-testid={`offline-queue-drop-${q.id}`}>×</button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
